import React, { useState, useEffect, useMemo, useCallback, Fragment } from 'react';
import { fetchDashboardData, AUTO_REFRESH_SECONDS } from '../../operations/jiraService';
import { DashboardData, Issue } from './types';

// ─── Design Tokens ──────────────────────────────────────────────────────────

const Z = {
  purple: "#6c00f6", cyan: "#00b8ff", bg: "#F7F7F8",
  card: "#FFFFFF", border: "#E8E8EC", borderLight: "#F0F0F3",
  text: "#1A1A2E", sub: "#6B7280", mute: "#9999AD",
  green: "#22C55E", orange: "#F59E0B", red: "#EF4444",
  font: "'Plus Jakarta Sans', sans-serif",
  mono: "'JetBrains Mono', monospace",
};

// ─── Status Configuration ───────────────────────────────────────────────────

const STATUS_ORDER: string[] = [
  "To Do", "In Progress", "Feature Branch", "Unit Testing",
  "Dev Deployment", "Dev QA", "UAT Deployment", "UAT QA",
  "Ready for Prod Deployment", "DEPLOYMENT IN PRODUCTON", "Done",
];

interface StatusConfig {
  color: string;
  bg: string;
  label: string;
}

const STATUS_MAP: Record<string, StatusConfig> = {
  "To Do":                     { color: "#4338CA", bg: "#EEF2FF", label: "To Do" },
  "In Progress":               { color: "#0D9488", bg: "#F0FDFA", label: "In Progress" },
  "Feature Branch":            { color: "#2563EB", bg: "#EFF6FF", label: "Feature Branch" },
  "Unit Testing":              { color: "#EA580C", bg: "#FFF7ED", label: "Unit Testing" },
  "Dev Deployment":            { color: "#7C3AED", bg: "#F5F3FF", label: "Dev Deployment" },
  "Dev QA":                    { color: "#DC2626", bg: "#FEF2F2", label: "Dev QA" },
  "UAT Deployment":            { color: "#A855F7", bg: "#FDF4FF", label: "UAT" },
  "UAT QA":                    { color: "#E11D48", bg: "#FFF1F2", label: "UAT QA" },
  "Ready for Prod Deployment": { color: "#D97706", bg: "#FFFBEB", label: "Ready Prod" },
  "DEPLOYMENT IN PRODUCTON":   { color: "#059669", bg: "#ECFDF5", label: "Production" },
  "Done":                      { color: "#16A34A", bg: "#F0FDF4", label: "Done" },
};

interface StatusGroupColor {
  bg: string;
  text: string;
}

const STATUS_GROUP_COLORS: Record<string, StatusGroupColor> = {
  todo:       { bg: "#EEF2FF", text: "#4338CA" },
  dev:        { bg: "#F0FDFA", text: "#0D9488" },
  testing:    { bg: "#FFF7ED", text: "#EA580C" },
  uat:        { bg: "#FDF4FF", text: "#A855F7" },
  readyProd:  { bg: "#FFFBEB", text: "#D97706" },
  production: { bg: "#ECFDF5", text: "#059669" },
  done:       { bg: "#F0FDF4", text: "#16A34A" },
};

const TABS: string[] = ["Sprint Overview", "Team Performance", "Quality & Tracking"];

interface TeamStatusCol {
  key: string;
  label: string;
}

const TEAM_STATUS_COLS: TeamStatusCol[] = [
  { key: "To Do",                     label: "To Do" },
  { key: "In Progress",               label: "In Prog" },
  { key: "Feature Branch",            label: "Feature Br" },
  { key: "Unit Testing",              label: "Unit Test" },
  { key: "Dev Deployment",            label: "Dev Deploy" },
  { key: "Dev QA",                    label: "Dev QA" },
  { key: "UAT Deployment",            label: "UAT" },
  { key: "UAT QA",                    label: "UAT QA" },
  { key: "Ready for Prod Deployment", label: "Ready Prod" },
  { key: "DEPLOYMENT IN PRODUCTON",   label: "Prod" },
  { key: "Done",                      label: "Done" },
];

// ─── SVG Chart Components ───────────────────────────────────────────────────

interface StatusBarChartProps {
  counts: Record<string, number>;
}

function StatusBarChart({ counts }: StatusBarChartProps) {
  const statuses = STATUS_ORDER.filter(s => (counts[s] || 0) > 0 || true); // show all
  const max = Math.max(...statuses.map(s => counts[s] || 0), 1);
  const W = 620, H = 220, P = { l: 36, r: 10, t: 10, b: 60 };
  const cW = W - P.l - P.r, cH = H - P.t - P.b;
  const n = statuses.length;
  const barW = Math.min(36, (cW / n) * 0.6);
  const gap = cW / n;

  return (
    <svg viewBox={`0 0 ${W} ${H}`} style={{ width: "100%" }}>
      {[0, 25, 50, 75, 100].map(pct => {
        const y = P.t + cH * (1 - pct / 100);
        return <g key={pct}>
          <line x1={P.l} x2={W - P.r} y1={y} y2={y} stroke={Z.borderLight} strokeWidth={.5} />
          <text x={P.l - 4} y={y + 3} textAnchor="end" fontSize={8} fill={Z.mute} fontFamily={Z.mono}>{Math.round(max * pct / 100)}</text>
        </g>;
      })}
      {statuses.map((s, i) => {
        const v = counts[s] || 0;
        const cfg = STATUS_MAP[s] || { color: "#888", bg: "#eee", label: s };
        const barH = (v / max) * cH;
        const x = P.l + i * gap + (gap - barW) / 2;
        const y = P.t + cH - barH;
        return <g key={s}>
          <rect x={x} y={y} width={barW} height={Math.max(barH, 0)} fill={cfg.color} rx={4} />
          {v > 0 && <text x={x + barW / 2} y={y - 5} textAnchor="middle" fontSize={10} fontWeight={700} fill={cfg.color} fontFamily={Z.mono}>{v}</text>}
          <text x={x + barW / 2} y={P.t + cH + 14} textAnchor="middle" fontSize={7} fill={Z.sub} fontFamily={Z.font} fontWeight={600}>{cfg.label}</text>
        </g>;
      })}
    </svg>
  );
}

// ─── Shared UI Components ───────────────────────────────────────────────────

interface CardProps {
  title?: string;
  subtitle?: string;
  children: React.ReactNode;
  style?: React.CSSProperties;
}

function Card({ title, subtitle, children, style: extraStyle }: CardProps) {
  return (
    <div style={{ background: Z.card, borderRadius: 14, border: `1px solid ${Z.border}`, padding: "20px 24px", marginBottom: 16, ...extraStyle }}>
      {title && <div style={{ fontSize: 15, fontWeight: 700, color: Z.text, marginBottom: subtitle ? 2 : 14 }}>{title}</div>}
      {subtitle && <div style={{ fontSize: 12, color: Z.sub, marginBottom: 14 }}>{subtitle}</div>}
      {children}
    </div>
  );
}

interface MetricCardProps {
  label: string;
  value: string | number;
  sub?: string;
  color?: string;
}

function MetricCard({ label, value, sub, color }: MetricCardProps) {
  return (
    <div style={{ background: Z.card, borderRadius: 12, border: `1px solid ${Z.border}`, padding: "16px 20px", textAlign: "center" }}>
      <div style={{ fontSize: 11, color: Z.sub, fontWeight: 600, marginBottom: 6, textTransform: "uppercase", letterSpacing: 0.5 }}>{label}</div>
      <div style={{ fontSize: 28, fontWeight: 800, color: color || Z.text }}>{value}</div>
      {sub && <div style={{ fontSize: 11, color: Z.sub, marginTop: 4 }}>{sub}</div>}
    </div>
  );
}

interface InitialsProps {
  name: string;
}

function Initials({ name }: InitialsProps) {
  const parts = (name || "?").split(" ");
  const ini = parts.length >= 2 ? parts[0][0] + parts[1][0] : parts[0][0];
  return (
    <div style={{ width: 34, height: 34, borderRadius: "50%", background: `linear-gradient(135deg, ${Z.purple}, ${Z.cyan})`, color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 700, flexShrink: 0 }}>
      {ini.toUpperCase()}
    </div>
  );
}

interface StatusBadgeProps {
  status: string;
}

function StatusBadge({ status }: StatusBadgeProps) {
  const mapped = STATUS_MAP[status];
  const fallback = STATUS_GROUP_COLORS.todo;
  const bg = mapped ? mapped.bg : fallback.bg;
  const color = mapped ? mapped.color : fallback.text;
  return (
    <span style={{ display: "inline-block", padding: "2px 10px", borderRadius: 99, fontSize: 11, fontWeight: 600, background: bg, color, whiteSpace: "nowrap" }}>
      {STATUS_MAP[status]?.label || status}
    </span>
  );
}

// ─── Dev type used internally ───────────────────────────────────────────────

interface DevEntry {
  name: string;
  issues: Issue[];
  backlogIssues: Issue[];
  totalSP: number;
}

// ─── Main Dashboard ─────────────────────────────────────────────────────────

export default function ProductivityDashboard() {
  const [data, setData] = useState<DashboardData | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [countdown, setCountdown] = useState<number>(AUTO_REFRESH_SECONDS);
  const [activeTab, setActiveTab] = useState<number>(0);
  const [projectFilter, setProjectFilter] = useState<string>("ALL");
  const [expandedStaff, setExpandedStaff] = useState<Record<string, boolean>>({});
  const [expandedReporter, setExpandedReporter] = useState<Record<string, boolean>>({});

  const refresh = useCallback(async () => {
    try {
      setErr(null);
      const d = await fetchDashboardData();
      setData(d);
    } catch (error: unknown) {
      setErr(error instanceof Error ? error.message : String(error));
    } finally {
      setLoading(false);
      setCountdown(AUTO_REFRESH_SECONDS);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);
  useEffect(() => {
    const t = setInterval(() => {
      setCountdown((p: number) => {
        if (p <= 1) { refresh(); return AUTO_REFRESH_SECONDS; }
        return p - 1;
      });
    }, 1000);
    return () => clearInterval(t);
  }, [refresh]);

  // --- Client-side project filtering ---
  const filteredIssues = useMemo((): Issue[] => {
    if (!data?.issues) return [];
    if (projectFilter === "ALL") return data.issues;
    return data.issues.filter((i: Issue) => i.project === projectFilter);
  }, [data?.issues, projectFilter]);

  // Individual status counts
  const filteredStatusCounts = useMemo((): Record<string, number> => {
    const counts: Record<string, number> = {};
    STATUS_ORDER.forEach((s: string) => { counts[s] = 0; });
    filteredIssues.forEach((i: Issue) => {
      const s = i.status;
      counts[s] = (counts[s] || 0) + 1;
    });
    return counts;
  }, [filteredIssues]);

  const filteredBugMetrics = useMemo(() => {
    const bugs = filteredIssues.filter((i: Issue) => (i.type || "").toLowerCase() === "bug");
    const isSolved = (i: Issue): boolean => i.statusGroup === "done" || i.statusGroup === "production" || i.status === "Dev Deployment";
    return {
      raised: bugs.length,
      solved: bugs.filter(isSolved).length,
      hotfixes: bugs.filter((i: Issue) => (i.priority || "").toLowerCase() === "highest" || (i.summary || "").toLowerCase().includes("hotfix")).length,
    };
  }, [filteredIssues]);

  const filteredTypeMetrics = useMemo(() => {
    const tm = { tasks: { total: 0, solved: 0 }, epics: { total: 0, closed: 0 }, bugs: { total: 0, solved: 0 } };
    const isBugSolved = (i: Issue): boolean => i.statusGroup === "done" || i.statusGroup === "production" || i.status === "Dev Deployment";
    for (const issue of filteredIssues) {
      const t = (issue.type || "").toLowerCase();
      if (t === "bug") { tm.bugs.total++; if (isBugSolved(issue)) tm.bugs.solved++; }
      else if (t === "epic") { tm.epics.total++; if (issue.statusGroup === "done" || issue.statusGroup === "production") tm.epics.closed++; }
      else { tm.tasks.total++; if (issue.statusGroup === "done" || issue.statusGroup === "production") tm.tasks.solved++; }
    }
    return tm;
  }, [filteredIssues]);

  const filteredDevs = useMemo((): DevEntry[] => {
    const devMap: Record<string, DevEntry> = {};
    filteredIssues.forEach((i: Issue) => {
      const a = i.assignee;
      if (!devMap[a]) devMap[a] = { name: a, issues: [], backlogIssues: [], totalSP: 0 };
      devMap[a].issues.push(i);
      devMap[a].totalSP += i.sp;
    });
    return Object.values(devMap).sort((a, b) => b.issues.length - a.issues.length);
  }, [filteredIssues]);

  const filteredBacklogIssues = useMemo((): Issue[] => {
    if (!data?.backlogIssues) return [];
    if (projectFilter === "ALL") return data.backlogIssues;
    return data.backlogIssues.filter((i: Issue) => i.project === projectFilter);
  }, [data?.backlogIssues, projectFilter]);

  // Merge sprint devs + backlog-only devs
  const allStaffDevs = useMemo((): DevEntry[] => {
    const devMap: Record<string, DevEntry> = {};
    filteredDevs.forEach((d: DevEntry) => { devMap[d.name] = { ...d }; });
    filteredBacklogIssues.forEach((i: Issue) => {
      const a = i.assignee;
      if (!devMap[a]) devMap[a] = { name: a, issues: [], backlogIssues: [], totalSP: 0 };
      if (!devMap[a].backlogIssues) devMap[a].backlogIssues = [];
      devMap[a].backlogIssues.push(i);
    });
    return Object.values(devMap).sort((a, b) => b.issues.length - a.issues.length);
  }, [filteredDevs, filteredBacklogIssues]);

  // Per-dev status counts for team table
  const devStatusCounts = useMemo((): Record<string, Record<string, number>> => {
    const result: Record<string, Record<string, number>> = {};
    for (const dev of allStaffDevs) {
      const counts: Record<string, number> = {};
      STATUS_ORDER.forEach((s: string) => { counts[s] = 0; });
      dev.issues.forEach((i: Issue) => { counts[i.status] = (counts[i.status] || 0) + 1; });
      result[dev.name] = counts;
    }
    return result;
  }, [allStaffDevs]);

  // Loading state
  if (loading && !data) {
    return (
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100vh", fontFamily: Z.font, color: Z.sub }}>
        <div style={{ textAlign: "center" }}>
          <div style={{ width: 40, height: 40, border: `3px solid ${Z.border}`, borderTopColor: Z.purple, borderRadius: "50%", animation: "spin 1s linear infinite", margin: "0 auto 16px" }} />
          <style>{`@keyframes spin { to { transform: rotate(360deg) } }`}</style>
          Loading dashboard...
        </div>
      </div>
    );
  }

  if (err && !data) {
    return (
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100vh", fontFamily: Z.font }}>
        <div style={{ textAlign: "center", color: Z.red }}>
          <div style={{ fontSize: 18, fontWeight: 600, marginBottom: 8 }}>Failed to load</div>
          <div style={{ fontSize: 14, color: Z.sub, marginBottom: 16 }}>{err}</div>
          <button onClick={refresh} style={{ padding: "8px 20px", borderRadius: 8, border: "none", background: Z.purple, color: "#fff", fontWeight: 600, cursor: "pointer" }}>Retry</button>
        </div>
      </div>
    );
  }

  const { sprintStatus: ss, reporters, backlogByAssignee, sprintNamesByProject } = data!;
  const mins = Math.floor(countdown / 60);
  const secs = countdown % 60;

  const projects: string[] = ["ALL", ...Array.from(new Set((data!.issues || []).map((i: Issue) => i.project).filter(Boolean)))];

  const displaySprintName = projectFilter === "ALL"
    ? ss?.name
    : (sprintNamesByProject?.[projectFilter] || []).join(" + ") || ss?.name;

  const filteredTotalSP = filteredIssues.reduce((s: number, i: Issue) => s + i.sp, 0);
  const filteredDoneSP = filteredIssues.filter((i: Issue) => i.statusGroup === "done" || i.statusGroup === "production").reduce((s: number, i: Issue) => s + i.sp, 0);
  const filteredDoneCount = filteredIssues.filter((i: Issue) => i.statusGroup === "done" || i.statusGroup === "production").length;
  const filteredPctComplete = filteredTotalSP > 0
    ? Math.round((filteredDoneSP / filteredTotalSP) * 100)
    : filteredIssues.length > 0
      ? Math.round((filteredDoneCount / filteredIssues.length) * 100)
      : 0;

  return (
    <div style={{ fontFamily: Z.font, color: Z.text, maxWidth: 1400, margin: "0 auto", padding: "20px 32px 60px" }}>
      <style>{`@keyframes spin { to { transform: rotate(360deg) } }`}</style>

      {/* HEADER */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16, flexWrap: "wrap", gap: 12 }}>
        <h1 style={{ margin: 0, fontSize: 24, fontWeight: 800, color: Z.text }}>Sprint Productivity Dashboard</h1>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <span style={{ fontSize: 12, color: Z.sub, fontWeight: 600 }}>Project:</span>
            <select
              value={projectFilter}
              onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setProjectFilter(e.target.value)}
              style={{ padding: "6px 12px", borderRadius: 8, border: `1px solid ${Z.border}`, background: Z.card, fontSize: 13, fontWeight: 600, cursor: "pointer", color: Z.text, fontFamily: Z.font }}
            >
              {projects.map((p: string) => <option key={p} value={p}>{p === "ALL" ? "All Projects" : p}</option>)}
            </select>
          </div>
          <button onClick={refresh} style={{ padding: "6px 16px", borderRadius: 8, border: `1px solid ${Z.border}`, background: Z.card, fontSize: 13, fontWeight: 600, cursor: "pointer", color: Z.text }}>Refresh</button>
          <span style={{ fontSize: 12, fontFamily: Z.mono, color: Z.sub }}>{mins}:{secs.toString().padStart(2, "0")}</span>
        </div>
      </div>

      {/* TAB BAR */}
      <div style={{ display: "flex", gap: 4, marginBottom: 24, borderBottom: `2px solid ${Z.border}`, paddingBottom: 0 }}>
        {TABS.map((tab: string, i: number) => (
          <button
            key={tab}
            onClick={() => setActiveTab(i)}
            style={{
              padding: "10px 20px", fontSize: 13, fontWeight: 700, cursor: "pointer",
              border: "none", borderBottom: `3px solid ${activeTab === i ? Z.purple : "transparent"}`,
              background: "transparent", color: activeTab === i ? Z.purple : Z.sub,
              fontFamily: Z.font, transition: "all 0.2s", marginBottom: -2,
            }}
          >
            {tab}
          </button>
        ))}
      </div>

      {/* TAB 1: Sprint Overview */}
      {activeTab === 0 && (
        <div>
          {ss ? (
            <Card title={displaySprintName} subtitle={`${new Date(ss.startDate).toLocaleDateString()} — ${new Date(ss.endDate).toLocaleDateString()} · ${ss.daysRemaining} days remaining`}>
              <div style={{ marginBottom: 18 }}>
                <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13, fontWeight: 600, marginBottom: 6 }}>
                  <span>{filteredPctComplete}% complete</span>
                  <span style={{ color: Z.sub }}>{filteredTotalSP > 0 ? `${filteredDoneSP} / ${filteredTotalSP} SP` : `${filteredDoneCount} / ${filteredIssues.length} issues`}</span>
                </div>
                <div style={{ height: 10, borderRadius: 99, background: Z.border, overflow: "hidden" }}>
                  <div style={{ height: "100%", width: `${filteredPctComplete}%`, borderRadius: 99, background: `linear-gradient(90deg, ${Z.purple}, ${Z.cyan})`, transition: "width 0.5s" }} />
                </div>
              </div>
              {/* Individual status pills */}
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                {STATUS_ORDER.map((s: string) => {
                  const cfg = STATUS_MAP[s];
                  const count = filteredStatusCounts[s] || 0;
                  return (
                    <div key={s} style={{ display: "flex", alignItems: "center", gap: 5, padding: "6px 12px", borderRadius: 10, background: cfg.bg, border: `1px solid ${cfg.bg}` }}>
                      <span style={{ fontSize: 16, fontWeight: 800, color: cfg.color }}>{count}</span>
                      <span style={{ fontSize: 11, fontWeight: 600, color: cfg.color }}>{cfg.label}</span>
                    </div>
                  );
                })}
                <div style={{ display: "flex", alignItems: "center", gap: 5, padding: "6px 12px", borderRadius: 10, background: "#F3F4F6" }}>
                  <span style={{ fontSize: 16, fontWeight: 800, color: Z.text }}>{filteredIssues.length}</span>
                  <span style={{ fontSize: 11, fontWeight: 600, color: Z.sub }}>Total</span>
                </div>
              </div>
            </Card>
          ) : (
            <Card><div style={{ color: Z.sub, fontSize: 14 }}>No active sprint found.</div></Card>
          )}

          <Card title="Status Distribution">
            <StatusBarChart counts={filteredStatusCounts} />
          </Card>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 12, marginTop: 8 }}>
            <MetricCard label="Bugs" value={`${filteredBugMetrics.solved}/${filteredBugMetrics.raised}`} sub="solved / raised" color={filteredBugMetrics.raised > filteredBugMetrics.solved ? Z.red : Z.green} />
            <MetricCard label="Hotfixes" value={filteredBugMetrics.hotfixes} sub="critical / urgent" color={filteredBugMetrics.hotfixes > 0 ? Z.orange : Z.green} />
            <MetricCard label="Tasks" value={`${filteredTypeMetrics.tasks.solved}/${filteredTypeMetrics.tasks.total}`} sub="solved / total" color={Z.cyan} />
            <MetricCard label="Epics" value={`${filteredTypeMetrics.epics.closed}/${filteredTypeMetrics.epics.total}`} sub="closed / total" color={Z.purple} />
          </div>
        </div>
      )}

      {/* TAB 2: Team Performance */}
      {activeTab === 1 && (
        <div>
          <Card title="Team Performance" subtitle="Current Jira status breakdown per team member">
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12, minWidth: 900 }}>
                <thead>
                  <tr style={{ borderBottom: `2px solid ${Z.border}` }}>
                    <th style={{ textAlign: "left", padding: "8px 6px", fontWeight: 600, color: Z.sub, fontSize: 10, textTransform: "uppercase", position: "sticky", left: 0, background: Z.card, zIndex: 1 }}>Team Member</th>
                    <th style={{ textAlign: "center", padding: "8px 4px", fontWeight: 700, color: Z.text, fontSize: 10, textTransform: "uppercase" }}>Total</th>
                    <th style={{ textAlign: "center", padding: "8px 4px", fontWeight: 600, color: Z.orange, fontSize: 10, textTransform: "uppercase" }}>Backlog</th>
                    {TEAM_STATUS_COLS.map((col: TeamStatusCol) => {
                      const cfg = STATUS_MAP[col.key];
                      return <th key={col.key} style={{ textAlign: "center", padding: "8px 3px", fontWeight: 600, color: cfg?.color || Z.sub, fontSize: 9, textTransform: "uppercase", whiteSpace: "nowrap" }}>{col.label}</th>;
                    })}
                  </tr>
                </thead>
                <tbody>
                  {allStaffDevs.filter((d: DevEntry) => d.name !== "Unassigned").map((dev: DevEntry) => {
                    const sc = devStatusCounts[dev.name] || {};
                    const blCount = (dev.backlogIssues || []).length || (backlogByAssignee?.[dev.name] || 0);
                    return (
                    <Fragment key={dev.name}>
                      <tr
                        style={{ borderBottom: `1px solid ${Z.border}`, cursor: "pointer", background: dev.issues.length === 0 ? "#FFFBEB" : "transparent" }}
                        onClick={() => setExpandedStaff((prev: Record<string, boolean>) => ({ ...prev, [dev.name]: !prev[dev.name] }))}
                      >
                        <td style={{ padding: "8px 6px", position: "sticky", left: 0, background: dev.issues.length === 0 ? "#FFFBEB" : Z.card, zIndex: 1 }}>
                          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                            <Initials name={dev.name} />
                            <div>
                              <span style={{ fontWeight: 600, fontSize: 12 }}>{dev.name}</span>
                              {dev.issues.length === 0 && <span style={{ fontSize: 9, color: Z.orange, marginLeft: 4 }}>backlog only</span>}
                              <div style={{ fontSize: 9, color: Z.mute }}>{expandedStaff[dev.name] ? "collapse" : "expand"}</div>
                            </div>
                          </div>
                        </td>
                        <td style={{ padding: "8px 4px", textAlign: "center", fontWeight: 800, fontSize: 14, color: Z.text }}>{dev.issues.length}</td>
                        <td style={{ padding: "8px 4px", textAlign: "center", fontWeight: 700, fontSize: 13, color: blCount > 0 ? Z.orange : Z.mute }}>{blCount}</td>
                        {TEAM_STATUS_COLS.map((col: TeamStatusCol) => {
                          const v = sc[col.key] || 0;
                          const cfg = STATUS_MAP[col.key];
                          return (
                            <td key={col.key} style={{ padding: "6px 3px", textAlign: "center" }}>
                              <span style={{
                                display: "inline-block", minWidth: 24, padding: "3px 6px", borderRadius: 6,
                                background: v > 0 ? cfg?.bg || "#F9FAFB" : "transparent",
                                color: v > 0 ? cfg?.color || Z.sub : Z.mute,
                                fontWeight: 700, fontSize: 12,
                              }}>
                                {v > 0 ? v : ""}
                              </span>
                            </td>
                          );
                        })}
                      </tr>
                      {expandedStaff[dev.name] && (
                        <tr>
                          <td colSpan={3 + TEAM_STATUS_COLS.length} style={{ padding: "0 8px 12px 56px", background: "#FAFAFA" }}>
                            {dev.issues.length > 0 && <div style={{ fontSize: 10, fontWeight: 700, color: Z.sub, marginTop: 8, marginBottom: 4 }}>SPRINT ISSUES</div>}
                            {dev.issues.map((issue: Issue) => (
                              <div key={issue.key} style={{ display: "flex", alignItems: "center", gap: 8, padding: "4px 0", borderBottom: `1px solid ${Z.borderLight}`, fontSize: 12 }}>
                                <span style={{ fontFamily: Z.mono, fontWeight: 600, color: Z.purple, fontSize: 11, minWidth: 70 }}>{issue.key}</span>
                                <span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", color: Z.text }}>{issue.summary}</span>
                                <StatusBadge status={issue.status} />
                              </div>
                            ))}
                            {(dev.backlogIssues || []).length > 0 && (
                              <>
                                <div style={{ fontSize: 10, fontWeight: 700, color: Z.orange, marginTop: 10, marginBottom: 4 }}>BACKLOG (Not in Sprint)</div>
                                {dev.backlogIssues.map((issue: Issue) => (
                                  <div key={issue.key} style={{ display: "flex", alignItems: "center", gap: 8, padding: "4px 0", borderBottom: `1px solid ${Z.borderLight}`, fontSize: 12 }}>
                                    <span style={{ fontFamily: Z.mono, fontWeight: 600, color: Z.orange, fontSize: 11, minWidth: 70 }}>{issue.key}</span>
                                    <span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", color: Z.text }}>{issue.summary}</span>
                                    <StatusBadge status={issue.status} />
                                  </div>
                                ))}
                              </>
                            )}
                          </td>
                        </tr>
                      )}
                    </Fragment>
                    );
                  })}
                  {/* Total row */}
                  {allStaffDevs.filter((d: DevEntry) => d.name !== "Unassigned").length > 0 && (
                    <tr style={{ borderTop: `2px solid ${Z.text}`, background: "#FAFAFA" }}>
                      <td style={{ padding: "10px 6px", fontWeight: 800, fontSize: 12, position: "sticky", left: 0, background: "#FAFAFA", zIndex: 1 }}>Total</td>
                      <td style={{ padding: "10px 4px", textAlign: "center", fontWeight: 800, fontSize: 14 }}>{allStaffDevs.filter((d: DevEntry) => d.name !== "Unassigned").reduce((s: number, d: DevEntry) => s + d.issues.length, 0)}</td>
                      <td style={{ padding: "10px 4px", textAlign: "center", fontWeight: 800, fontSize: 14, color: Z.orange }}>{allStaffDevs.filter((d: DevEntry) => d.name !== "Unassigned").reduce((s: number, d: DevEntry) => s + ((d.backlogIssues || []).length || (backlogByAssignee?.[d.name] || 0)), 0)}</td>
                      {TEAM_STATUS_COLS.map((col: TeamStatusCol) => {
                        const total = allStaffDevs.filter((d: DevEntry) => d.name !== "Unassigned").reduce((s: number, d: DevEntry) => s + ((devStatusCounts[d.name] || {})[col.key] || 0), 0);
                        const cfg = STATUS_MAP[col.key];
                        return <td key={col.key} style={{ padding: "10px 3px", textAlign: "center", fontWeight: 800, fontSize: 12, color: total > 0 ? cfg?.color : Z.mute }}>{total > 0 ? total : ""}</td>;
                      })}
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </Card>

          {/* Cleared Jira this sprint */}
          <Card title="Cleared Jira in Current Sprint" subtitle="Issues that reached Done or Production">
            {filteredIssues.filter((i: Issue) => i.statusGroup === "done" || i.statusGroup === "production").length > 0 ? (
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                <thead>
                  <tr style={{ borderBottom: `2px solid ${Z.border}` }}>
                    <th style={{ textAlign: "left", padding: "6px 8px", fontWeight: 600, color: Z.sub, fontSize: 11, textTransform: "uppercase" }}>Key</th>
                    <th style={{ textAlign: "left", padding: "6px 8px", fontWeight: 600, color: Z.sub, fontSize: 11, textTransform: "uppercase" }}>Summary</th>
                    <th style={{ textAlign: "left", padding: "6px 8px", fontWeight: 600, color: Z.sub, fontSize: 11, textTransform: "uppercase" }}>Assignee</th>
                    <th style={{ textAlign: "left", padding: "6px 8px", fontWeight: 600, color: Z.sub, fontSize: 11, textTransform: "uppercase" }}>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredIssues.filter((i: Issue) => i.statusGroup === "done" || i.statusGroup === "production").map((issue: Issue) => (
                    <tr key={issue.key} style={{ borderBottom: `1px solid ${Z.border}` }}>
                      <td style={{ padding: "8px", fontFamily: Z.mono, fontWeight: 600, color: Z.purple, fontSize: 12 }}>{issue.key}</td>
                      <td style={{ padding: "8px", maxWidth: 360, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{issue.summary}</td>
                      <td style={{ padding: "8px", color: Z.sub }}>{issue.assignee}</td>
                      <td style={{ padding: "8px" }}><StatusBadge status={issue.status} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div style={{ color: Z.sub, fontSize: 14 }}>No issues completed yet in this sprint.</div>
            )}
          </Card>
        </div>
      )}

      {/* TAB 3: Quality & Tracking */}
      {activeTab === 2 && (
        <div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12, marginBottom: 16 }}>
            <MetricCard label="Bugs Raised" value={filteredBugMetrics.raised} color={Z.red} />
            <MetricCard label="Bugs Solved" value={filteredBugMetrics.solved} color={Z.green} sub="Done + Production + Dev Deployment" />
            <MetricCard label="Hotfixes" value={filteredBugMetrics.hotfixes} color={Z.orange} sub="Highest priority / hotfix" />
          </div>

          <Card title="Open Bugs (To Do)" subtitle="Bugs still in To Do status">
            {filteredIssues.filter((i: Issue) => (i.type || "").toLowerCase() === "bug" && i.statusGroup === "todo").length > 0 ? (
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                <thead>
                  <tr style={{ borderBottom: `2px solid ${Z.border}` }}>
                    <th style={{ textAlign: "left", padding: "6px 8px", fontWeight: 600, color: Z.sub, fontSize: 11, textTransform: "uppercase" }}>Key</th>
                    <th style={{ textAlign: "left", padding: "6px 8px", fontWeight: 600, color: Z.sub, fontSize: 11, textTransform: "uppercase" }}>Summary</th>
                    <th style={{ textAlign: "left", padding: "6px 8px", fontWeight: 600, color: Z.sub, fontSize: 11, textTransform: "uppercase" }}>Assignee</th>
                    <th style={{ textAlign: "left", padding: "6px 8px", fontWeight: 600, color: Z.sub, fontSize: 11, textTransform: "uppercase" }}>Priority</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredIssues.filter((i: Issue) => (i.type || "").toLowerCase() === "bug" && i.statusGroup === "todo").map((issue: Issue) => (
                    <tr key={issue.key} style={{ borderBottom: `1px solid ${Z.border}` }}>
                      <td style={{ padding: "8px", fontFamily: Z.mono, fontWeight: 600, color: Z.purple, fontSize: 12 }}>{issue.key}</td>
                      <td style={{ padding: "8px", maxWidth: 300, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{issue.summary}</td>
                      <td style={{ padding: "8px", color: Z.sub }}>{issue.assignee}</td>
                      <td style={{ padding: "8px", fontSize: 12, fontWeight: 600, color: issue.priority === "Highest" ? Z.red : issue.priority === "High" ? Z.orange : Z.sub }}>{issue.priority}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div style={{ color: Z.green, fontSize: 14 }}>No bugs in To Do.</div>
            )}
          </Card>

          {/* Production Deployments */}
          {(() => {
            const prodIssues = filteredIssues.filter((i: Issue) => i.statusGroup === "production");
            const prodBugs = prodIssues.filter((i: Issue) => (i.type || "").toLowerCase() === "bug");
            const prodTasks = prodIssues.filter((i: Issue) => { const t = (i.type || "").toLowerCase(); return t !== "bug" && t !== "epic"; });
            const prodEpics = prodIssues.filter((i: Issue) => (i.type || "").toLowerCase() === "epic");
            return (
              <Card title="Production Deployments" subtitle="Issues currently in production deployment">
                {prodIssues.length > 0 ? (
                  <div>
                    {[
                      { label: "Bugs", items: prodBugs, color: Z.red },
                      { label: "Tasks", items: prodTasks, color: Z.cyan },
                      { label: "Epics", items: prodEpics, color: Z.purple },
                    ].filter((g: { label: string; items: Issue[]; color: string }) => g.items.length > 0).map((group: { label: string; items: Issue[]; color: string }) => (
                      <div key={group.label} style={{ marginBottom: 16 }}>
                        <div style={{ fontSize: 13, fontWeight: 700, color: group.color, marginBottom: 8 }}>{group.label} ({group.items.length})</div>
                        {group.items.map((issue: Issue) => (
                          <div key={issue.key} style={{ display: "flex", alignItems: "center", gap: 8, padding: "5px 0", borderBottom: `1px solid ${Z.borderLight}`, fontSize: 12 }}>
                            <span style={{ fontFamily: Z.mono, fontWeight: 600, color: Z.purple, minWidth: 70 }}>{issue.key}</span>
                            <span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{issue.summary}</span>
                            <span style={{ color: Z.sub, fontSize: 11 }}>{issue.assignee}</span>
                          </div>
                        ))}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div style={{ color: Z.sub, fontSize: 14 }}>No production deployments yet.</div>
                )}
              </Card>
            );
          })()}

          {/* Raised by Stakeholders */}
          <Card title="Raised by Stakeholders" subtitle="Issues raised by key stakeholders across SMB, SMA, ZAP with current status">
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
              <thead>
                <tr style={{ borderBottom: `2px solid ${Z.border}` }}>
                  <th style={{ textAlign: "left", padding: "8px", fontWeight: 600, color: Z.sub, fontSize: 11, textTransform: "uppercase" }}>Stakeholder</th>
                  <th style={{ textAlign: "center", padding: "8px", fontWeight: 600, color: Z.sub, fontSize: 11, textTransform: "uppercase" }}>Total</th>
                  <th style={{ textAlign: "center", padding: "8px", fontWeight: 600, color: Z.red, fontSize: 11, textTransform: "uppercase" }}>Bugs</th>
                  <th style={{ textAlign: "center", padding: "8px", fontWeight: 600, color: Z.cyan, fontSize: 11, textTransform: "uppercase" }}>Tasks</th>
                  <th style={{ textAlign: "center", padding: "8px", fontWeight: 600, color: Z.purple, fontSize: 11, textTransform: "uppercase" }}>Epics</th>
                </tr>
              </thead>
              <tbody>
                {(reporters || []).map((r: { name: string; issues?: Issue[] }) => {
                  const rIssues = (r.issues || []).filter((i: Issue) => projectFilter === "ALL" || i.project === projectFilter);
                  const rBugs = rIssues.filter((i: Issue) => (i.type || "").toLowerCase() === "bug").length;
                  const rTasks = rIssues.filter((i: Issue) => { const t = (i.type || "").toLowerCase(); return t !== "bug" && t !== "epic"; }).length;
                  const rEpics = rIssues.filter((i: Issue) => (i.type || "").toLowerCase() === "epic").length;
                  return (
                  <Fragment key={r.name}>
                    <tr
                      style={{ borderBottom: `1px solid ${Z.border}`, borderLeft: `3px solid ${Z.purple}`, background: `${Z.purple}05`, cursor: "pointer" }}
                      onClick={() => setExpandedReporter((prev: Record<string, boolean>) => ({ ...prev, [r.name]: !prev[r.name] }))}
                    >
                      <td style={{ padding: "10px 8px" }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                          <Initials name={r.name} />
                          <div>
                            <span style={{ fontWeight: 600 }}>{r.name}</span>
                            <div style={{ fontSize: 10, color: Z.sub }}>{expandedReporter[r.name] ? "click to collapse" : "click to see issues"}</div>
                          </div>
                        </div>
                      </td>
                      <td style={{ padding: "10px 8px", textAlign: "center", fontWeight: 700, fontSize: 16 }}>{rIssues.length}</td>
                      <td style={{ padding: "10px 8px", textAlign: "center", fontWeight: 700, fontSize: 16, color: rBugs > 0 ? Z.red : Z.sub }}>{rBugs}</td>
                      <td style={{ padding: "10px 8px", textAlign: "center", fontWeight: 700, fontSize: 16, color: rTasks > 0 ? Z.cyan : Z.sub }}>{rTasks}</td>
                      <td style={{ padding: "10px 8px", textAlign: "center", fontWeight: 700, fontSize: 16, color: rEpics > 0 ? Z.purple : Z.sub }}>{rEpics}</td>
                    </tr>
                    {expandedReporter[r.name] && rIssues.length > 0 && (
                      <tr>
                        <td colSpan={5} style={{ padding: "0 8px 12px 56px", background: "#FAFAFA" }}>
                          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12, marginTop: 4 }}>
                            <thead>
                              <tr style={{ borderBottom: `1px solid ${Z.border}` }}>
                                <th style={{ textAlign: "left", padding: "4px 6px", fontWeight: 600, color: Z.sub, fontSize: 10, textTransform: "uppercase" }}>Key</th>
                                <th style={{ textAlign: "left", padding: "4px 6px", fontWeight: 600, color: Z.sub, fontSize: 10, textTransform: "uppercase" }}>Type</th>
                                <th style={{ textAlign: "left", padding: "4px 6px", fontWeight: 600, color: Z.sub, fontSize: 10, textTransform: "uppercase" }}>Summary</th>
                                <th style={{ textAlign: "left", padding: "4px 6px", fontWeight: 600, color: Z.sub, fontSize: 10, textTransform: "uppercase" }}>Assignee</th>
                                <th style={{ textAlign: "left", padding: "4px 6px", fontWeight: 600, color: Z.sub, fontSize: 10, textTransform: "uppercase" }}>Status</th>
                              </tr>
                            </thead>
                            <tbody>
                              {rIssues.map((issue: Issue) => (
                                <tr key={issue.key} style={{ borderBottom: `1px solid ${Z.borderLight}` }}>
                                  <td style={{ padding: "6px", fontFamily: Z.mono, fontWeight: 600, color: Z.purple, fontSize: 11, whiteSpace: "nowrap" }}>{issue.key}</td>
                                  <td style={{ padding: "6px", fontSize: 11, color: (issue.type || "").toLowerCase() === "bug" ? Z.red : Z.sub }}>{issue.type}</td>
                                  <td style={{ padding: "6px", maxWidth: 280, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{issue.summary}</td>
                                  <td style={{ padding: "6px", color: Z.sub, fontSize: 11, whiteSpace: "nowrap" }}>{issue.assignee}</td>
                                  <td style={{ padding: "6px" }}><StatusBadge status={issue.status} /></td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                  );
                })}
              </tbody>
            </table>
          </Card>
        </div>
      )}

      {/* FOOTER */}
      <div style={{ textAlign: "center", fontSize: 12, color: Z.sub, marginTop: 32 }}>
        Last updated: {data!.timestamp ? new Date(data!.timestamp).toLocaleString() : "\u2014"}
      </div>
    </div>
  );
}
